import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import type { ResponseError } from "../types/Errors";
import type Button from "sap/m/Button";
import { problemMessage } from "../util/ProblemDetails";

interface LrDeadLetter {
	id: string;
	destination: string;
	eventType: string;
	route: string;
	attempts: number;
	createdAt: string;
	lastError: string | null;
}

/**
 * LR outbox dead-letter queue — messages the portal stopped trying to deliver
 * (GET /api/v1/lr-outbox/dead-letters; CompuTec-only, gated on lr-outbox.read). Each row can be
 * returned to the queue with lr-outbox.retry. Without this page a dead-lettered message is a row
 * nobody queries, which is indistinguishable from no data loss at all.
 *
 * Plain JSONModel + getAuthFetch, like Statistics: lr_outbox is a technical table and deliberately
 * has no OData entity.
 */
export default class LrDeadLetters extends BaseController {
	public onInit(): void {
		const model = new JSONModel({ loaded: false, items: [] });
		this.getView()?.setModel(model, "dlq");
		this.getRouter().getRoute("lrDeadLetters")?.attachPatternMatched(() => { void this.loadItems(); });
	}

	public onRefresh(): void {
		void this.loadItems();
	}

	private async loadItems(): Promise<void> {
		const model = this.getView()?.getModel("dlq") as JSONModel;
		model.setProperty("/loaded", false);
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const items = await fetcher.json<LrDeadLetter[]>("/api/v1/lr-outbox/dead-letters");
			model.setProperty("/items", Array.isArray(items) ? items : []);
		} catch (e) {
			const err = e as ResponseError;
			const bundle = await this.getResourceBundle();
			model.setProperty("/items", []);
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("lrDeadLetters.loadFailure") ?? "Failed"));
		} finally {
			model.setProperty("/loaded", true);
		}
	}

	public async onRetry(event: { getSource(): Button }): Promise<void> {
		const ctx = event.getSource().getBindingContext("dlq");
		if (!ctx) {
			return;
		}
		const row = ctx.getObject() as LrDeadLetter;
		const bundle = await this.getResourceBundle();

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.fetch(`/api/v1/lr-outbox/dead-letters/${row.id}/retry`, { method: "POST" });
			MessageToast.show(bundle.getText("lrDeadLetters.retried") ?? "Message returned to the queue");
			await this.loadItems();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("lrDeadLetters.retryFailure") ?? "Failed"));
		}
	}

	// Same shape as the copies in Deployments / Licenses / EntitlementDetail / DeploymentDetail /
	// DeploymentBindLicenses. Pulling all six into BaseController is worth doing, but it touches five
	// controllers this card has no business editing — reported as a separate candidate.
}
